/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {translate} from '@ion/core/src/locale/locale';
import {
  CarbDevices,
  CTCEntity,
  Devices,
  EntityStatus,
  GenericObject,
  SentinelDevices
} from '@ion/core/src/types/entities';
import Alert from '@ion/dls/src/components/alert/Alert';
import EmbeddedForm from '@ion/dls/src/editors/forms/EmbeddedForm';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {validateServices} from '../pages/devices/DeviceServicesUtils';
import ServiceEntry from '../pages/devices/ServiceEntry';
import CarbServiceForm from './carb/CarbServiceForm';
import {getPairedDevices, LMU_DEVICE_TYPE} from './BulkSelectEntitiesEditorUtils';
import SentinelForm from './SentinelForm';
import SentinelWarning from './SentinelWarning';

interface ServiceEnablementFormProps {
  selectedRowEntities?: CTCEntity[];
  setIsSentinel?: Function;
  setTaskNickNameAction: (action: string) => void;
  pairedDevices?: SentinelDevices;
  carbDevices?: CarbDevices;
  setCarbFinalDevices?: Function;
  selectedIdentifierType?: string;
  areTotalRowsSelected?: boolean;
  onSave: (details: ServiceDetails) => void;
  services?: string[];
  accountId?: number;
  setSentinelDevices?: Function;
  isSelectedFromGrid?: boolean;
  showSentinelWarning?: boolean;
  nonSentinelDevices?: Devices;
  setPairedDevices: Function;
  setNonSentinelDevices: Function;
  setIsProcessButtonDisabled?: Function;
}

const DDSAR = 'DDSAR';

export type ServiceDetails = {
  features: {
    [key: string]: string;
  };
}

export interface Entry {
  id: number;
  service: GenericObject;
}

const useStyles = makeStyles(() => ({
  errorMessageContainer : {
    padding : '0 0 5px 0'
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400
  }
}));

/**
 * Re-usable form with the details for Service Enablement of an entity.
 */
export const ServiceEnablementForm = forwardRef(({
  areTotalRowsSelected = false,
  showSentinelWarning = false,
  isSelectedFromGrid = false,
  nonSentinelDevices = [],
  setPairedDevices,
  setIsProcessButtonDisabled,
  setNonSentinelDevices,
  setTaskNickNameAction,
  selectedRowEntities,
  setSentinelDevices,
  setIsSentinel,
  pairedDevices,
  carbDevices,
  setCarbFinalDevices,
  selectedIdentifierType,
  accountId,
  services,
  onSave
}: ServiceEnablementFormProps, ref) => {
  const devicesRef     = useRef<SentinelDevices>(pairedDevices);
  const carbDevicesRef = useRef<CarbDevices>(carbDevices);
  const serviceOptions = services?.sort();
  const newEntry: Entry = {
    id      : 1,
    service : {
      name    : '',
      status  : EntityStatus.Enabled,
      options : serviceOptions || [],
      error   : false
    }
  };
  const [serviceEntry, setServiceEntry] = useState<Entry>(newEntry);
  const [error, setError]               = useState(false);
  const carb                            = translate('common:serviceEnablement.carb');
  const sentinelSynchronization         = translate('common:serviceEnablement.sentinel');

  const isSentinelService = useMemo(() => (
    serviceEntry?.service?.name === sentinelSynchronization), [serviceEntry]);

  const isCarbService = useMemo(() => (
    serviceEntry?.service?.name === carb), [serviceEntry]);
  
  const validateForm = function() {
    let hasServiceError   = false;
    const {isValid}       = validateServices([serviceEntry]);
    const isServiceActive = serviceEntry?.service?.status !== EntityStatus.Disabled;

    const isEmpty = (val) => !val?.toString()?.trim();

    if (isServiceActive) {
      if (isSentinelService) {
        hasServiceError = devicesRef?.current?.some(({secondaryDevice}) => isEmpty(secondaryDevice));
      }

      if (isCarbService) {
        hasServiceError = carbDevicesRef?.current?.some(({vin, licensePlate, licenseState}) =>
          isEmpty(vin) || isEmpty(licensePlate) || isEmpty(licenseState));
      }
    }
    setError(hasServiceError);

    return {isValid, isValidDevices: !hasServiceError};
  };

  useEffect(() => {
    if (isSelectedFromGrid && isSentinelService) {
      if (serviceEntry?.service?.status === EntityStatus.Enabled) {
        const {pairedDevices, unallowedDevices} = getPairedDevices(selectedRowEntities, 'esn', true);
        setPairedDevices(pairedDevices);
        setNonSentinelDevices(unallowedDevices);
      } else {
        const validDevices    = selectedRowEntities?.filter(({typeName}) => typeName === LMU_DEVICE_TYPE);
        const invalidDevices  = selectedRowEntities?.filter(({typeName}) => typeName !== LMU_DEVICE_TYPE);
        const {pairedDevices} = getPairedDevices(validDevices, 'esn', false);
        setPairedDevices(pairedDevices);
        setNonSentinelDevices(invalidDevices);
      }
    }
  }, [isSelectedFromGrid, isSentinelService, serviceEntry]);

  const updateEntry = useCallback(function(id: number, name: string, value: string): void {
    const updatedEntry = {...serviceEntry};
    updatedEntry.service[name] = value;
    updatedEntry.service.error = false;
    if (name === 'name') {
      setIsSentinel(value === sentinelSynchronization);
    }
    setError(false);
    setServiceEntry(updatedEntry);
    setTaskNickNameAction(getTaskNickNameAction(updatedEntry));
  }, [serviceEntry]);

  const onSubmit = function() {
    const {isValid, isValidDevices} = validateForm();
    if (isValid && isValidDevices) {
      const {name, status} = serviceEntry.service;
      const serviceName    = translate(`common:serviceEnablementValues.${name}`);
      if (isSentinelService) {
        setSentinelDevices?.(status === EntityStatus.Enabled ? devicesRef?.current : pairedDevices);
      }
      if (isCarbService) {
        setCarbFinalDevices?.(carbDevicesRef?.current);
      }
      onSave({
        features : {[serviceName]: status}
      });
    } else if (!isValid) {
      const updatedEntry = {...serviceEntry};
      updatedEntry.service.error = true;
      setServiceEntry(updatedEntry);
    }
  };

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  return (
    <EmbeddedForm>
      <Render.If condition={areTotalRowsSelected}>
        <Alert type={'warning'}
          message={translate('common:editors.bulkSentinel.disableSentinel')} />
      </Render.If>
      <Render.If condition={!areTotalRowsSelected &&
        showSentinelWarning && isSentinelService && nonSentinelDevices?.length}>
        <SentinelWarning nonSentinelDevices={nonSentinelDevices}/>
      </Render.If>
      <ServiceEntry id={1}
        onUpdate={updateEntry}
        service={serviceEntry?.service} />
      <Render.If condition={isSentinelService}>
        <SentinelForm error={error}
          devicesRef={devicesRef}
          devices={pairedDevices}
          accountId={accountId}
          identifier={selectedIdentifierType}
          isSelectedFromGrid={isSelectedFromGrid}
          serviceStatus={serviceEntry?.service?.status} />
      </Render.If>
      <Render.If condition={isCarbService}>
        <CarbServiceForm error={error}
          accountId={accountId}
          setIsProcessButtonDisabled={setIsProcessButtonDisabled}
          selectedIdentifierType={selectedIdentifierType}
          devices={carbDevices}
          isSelectedFromGrid={isSelectedFromGrid}
          devicesRef={carbDevicesRef}
          selectedRowEntities={selectedRowEntities}
          serviceStatus={serviceEntry?.service?.status} />
      </Render.If>
    </EmbeddedForm>
  );
});

const getTaskNickNameAction = (entry: Entry) => {
  const {service: {name, status}} = entry;
  const action = translate(`common:entityAction.${status === EntityStatus.Enabled ? 'enable' : 'disable'}`);
  const value  = translate(`common:serviceEnablementValues.${name}`);
  return `- ${action} ${value}`;
};

export default memo(ServiceEnablementForm);
